import moment from "moment";
import {
	STATUS_ACCEPTED,
	STATUS_IN_PROGRESS,
	STATUS_INVITED,
} from "utils/constants";
import { Tooltip } from "@chakra-ui/react";
import { cn, timezoneChecker } from "utils/helpers";

const SUBMISSION_STATUS_WARNINGS = [
	STATUS_ACCEPTED,
	STATUS_INVITED,
	STATUS_IN_PROGRESS,
];

const DEFAULT_RED_DAYS = 7;
const DEFAULT_WARNING_DAYS = 14;

export const hasSubmissionDeadlinePassed = (
	deadlineDateTime: moment.Moment,
	deadlineTimeZone: string
): boolean => {
	if (!deadlineDateTime) {
		return false;
	}

	const timeZone = getTimeZone(deadlineTimeZone);

	const now = moment().tz(timeZone).seconds(0);
	const target = moment(deadlineDateTime).tz(timeZone).seconds(0);
	return target.diff(now, "seconds") <= 0;
};

const getTimeZone = (deadlineTimeZone: string): string => {
	return timezoneChecker(deadlineTimeZone || "AWST");
};

const getDaysRemaining = (
	deadlineDateTime: Date,
	deadlineTimeZone: string
): number => {
	if (!deadlineDateTime) {
		return null;
	}

	const timeZone = getTimeZone(deadlineTimeZone);

	const now = moment().tz(timeZone).startOf("day");
	const target = moment(deadlineDateTime).tz(timeZone).startOf("day");
	return target.diff(now, "days");
};

const getColorClassNames = (
	daysRemaining: number,
	submissionStatus: string,
	warningDays: number
): string => {
	if (!SUBMISSION_STATUS_WARNINGS.includes(submissionStatus)) {
		return "";
	}

	if (daysRemaining <= DEFAULT_RED_DAYS) {
		return "text-red-600";
	} else if (daysRemaining <= warningDays) {
		return "text-orange-500 font-semibold";
	}
	return "";
};

const getDeadlineText = (
	daysRemaining: number,
	submissionStatus: string,
	deadlineDate: moment.Moment,
	formattedDateTime: string,
	useTwoLines: boolean,
	className: string,
	warningDays: number,
	showTimeUnderneath: boolean
): React.ReactNode => {
	if (!SUBMISSION_STATUS_WARNINGS.includes(submissionStatus)) {
		return formattedDateTime;
	}

	if (daysRemaining === null) {
		return "";
	}

	const colorClasses = getColorClassNames(
		daysRemaining,
		submissionStatus,
		warningDays
	);

	if (hasSubmissionDeadlinePassed(deadlineDate, deadlineDate.tz())) {
		return (
			<div className={cn(className, colorClasses)}>
				{formattedDateTime}
				{useTwoLines && <br />}
				{showTimeUnderneath && deadlineDate.format("h:mm A z")} (Past Due)
			</div>
		);
	} else if (daysRemaining === 0) {
		return (
			<div className={cn(className, colorClasses)}>
				DUE TODAY {deadlineDate.format("(D MMMM)")} {useTwoLines && <br />}
				{deadlineDate.format("h:mm A z")}
			</div>
		);
	} else {
		return (
			<div className={cn(className, colorClasses)}>
				{formattedDateTime}
				{useTwoLines && <br />}{" "}
				{showTimeUnderneath && (
					<span className="text-[12px]">{deadlineDate.format("h:mm A z")}</span>
				)}{" "}
				({daysRemaining} day{daysRemaining > 1 ? "s" : ""} remaining)
			</div>
		);
	}
};

interface SubmissionDeadlineProps {
	submissionStatus: string;
	deadlineDateTime: Date;
	deadlineTimeZone: string;
	dateFormat?: string;
	warningDays?: number;
	showTimeUnderneath: boolean;
	twoLines: boolean;
	className: string;
}

export const SubmissionDeadline: React.FC<SubmissionDeadlineProps> = ({
	submissionStatus,
	deadlineDateTime,
	deadlineTimeZone,
	dateFormat = "dddd, D MMMM YYYY",
	warningDays = DEFAULT_WARNING_DAYS,
	showTimeUnderneath = false,
	className = "",
	twoLines = true,
}) => {
	const safeTimeZone = getTimeZone(deadlineTimeZone);
	const daysRemaining = getDaysRemaining(deadlineDateTime, deadlineTimeZone);
	const deadlineDue = moment(deadlineDateTime).tz(safeTimeZone);
	const dateString = deadlineDue.format(dateFormat);

	const deadlineText = getDeadlineText(
		daysRemaining,
		submissionStatus,
		deadlineDue,
		dateString,
		twoLines,
		className,
		warningDays,
		showTimeUnderneath
	);

	let tooltipText = "";

	if (SUBMISSION_STATUS_WARNINGS.includes(submissionStatus)) {
		if (daysRemaining === 0) {
			tooltipText = "Submission is due today!";
		} else if (daysRemaining > 0) {
			tooltipText = `Submission is due within ${daysRemaining} days.`;
		} else {
			tooltipText = "Submission deadline has passed.";
		}
	}

	return (
		<Tooltip
			textColor={"white"}
			bgColor={"darkslategrey"}
			shouldWrapChildren={true}
			label={tooltipText}
			aria-label="Submission deadline tooltip"
		>
			{deadlineText}
		</Tooltip>
	);
};
